import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconBolt, IconLayers, IconTrash } from '../components/icons';
import { CodeTabs, Empty, Field, Skeleton, useAsync, useToast } from '../components/ui';
import { api, ApiError, Archetype, Draft, DraftListItem, Template, TemplatePreview } from '../lib/api';
import { ago } from '../lib/format';
import { canWrite, useSession } from '../lib/session';
import { t } from '../lib/i18n';

/**
 * Client-side emulation of a mail client's theme: the email's own dark rules are applied unconditionally for the
 * dark view and switched off for the light one, whatever theme the dashboard (and so the frame) is in.
 */
function forceScheme(html: string, scheme: 'light' | 'dark'): string {
  return html.replace(/@media\s*\(prefers-color-scheme:\s*dark\)/g, scheme === 'dark' ? '@media all' : '@media not all');
}

export function Code2EmailPage() {
  const { me } = useSession();
  const toast = useToast();
  const nav = useNavigate();
  const writable = canWrite(me);
  const ai = me?.platform.ai;

  const archetypes = useAsync(() => api.get<Archetype[]>('/code2email/archetypes'));
  const drafts = useAsync(() => api.get<DraftListItem[]>('/code2email/drafts'));
  const templates = useAsync(() => api.get<Template[]>('/templates'));

  const [form, setForm] = useState({ description: '', product_name: me?.workspace.name ?? '', brand_color: '#0b5cad', archetype: '', language: '', use_ai: true });
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [edit, setEdit] = useState({ name: '', alias: '', subject: '', html: '', text: '' });
  const [target, setTarget] = useState<string>('new');
  const [view, setView] = useState<'light' | 'dark' | 'text'>('light');
  const [preview, setPreview] = useState<TemplatePreview | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function load(d: Draft) {
    setDraft(d);
    setEdit({ name: d.name, alias: d.alias, subject: d.subject, html: d.html ?? '', text: d.text ?? '' });
    setTarget(d.published?.template_id ?? 'new');
    setErrors({});
  }

  async function generate(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const d = await api.post<Draft>('/code2email/drafts', {
        description: form.description,
        ...(form.product_name.trim() ? { product_name: form.product_name.trim() } : {}),
        brand_color: form.brand_color,
        ...(form.archetype ? { archetype: form.archetype } : {}),
        ...(form.language ? { language: form.language } : {}),
        use_ai: form.use_ai && Boolean(ai?.enabled),
      });
      load(d);
      drafts.reload();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  // Rendered with the sample variables through the production renderer, like the template editor.
  useEffect(() => {
    if (!draft) return;
    const t = setTimeout(() => {
      api
        .post<TemplatePreview>('/templates/preview', { subject: edit.subject, html: edit.html || null, text: edit.text || null, variables: draft.sample_variables })
        .then(setPreview)
        .catch(() => undefined);
    }, 250);
    return () => clearTimeout(t);
  }, [draft, edit.subject, edit.html, edit.text]);

  async function publish() {
    if (!draft) return;
    const existing = templates.data?.find((tp) => tp.id === target);
    const what = existing
      ? t('новую версию шаблона «{name}» (v{v}). Письма по алиасу «{alias}» сразу начнут уходить в новом виде.', { name: existing.name, v: existing.version + 1, alias: existing.alias })
      : t('новый шаблон «{name}» с алиасом «{alias}».', { name: edit.name, alias: edit.alias });
    if (!confirm(t('Опубликовать {what}', { what }))) return;
    setErrors({});
    try {
      const res = await api.post<{ template: Template }>(`/code2email/drafts/${draft.id}/publish`, {
        name: edit.name,
        alias: edit.alias,
        subject: edit.subject,
        html: edit.html || null,
        text: edit.text || null,
        ...(existing ? { template_id: existing.id } : {}),
      });
      toast(t('Опубликовано: {alias} v{v}', { alias: res.template.alias, v: res.template.version }));
      templates.reload();
      drafts.reload();
      nav(`/templates/${res.template.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.details) setErrors(Object.fromEntries(err.details.map((d) => [d.path, d.message])));
      toast(err instanceof ApiError && err.code === 'TEMPLATE_ALIAS_TAKEN' ? t('Такой алиас уже занят — выберите «новая версия» или смените алиас') : (err as Error).message, 'bad');
    }
  }

  async function remove(id: string) {
    if (!confirm(t('Удалить черновик? Опубликованные из него шаблоны останутся.'))) return;
    await api.del(`/code2email/drafts/${id}`);
    if (draft?.id === id) setDraft(null);
    drafts.reload();
  }

  const srcDoc = useMemo(() => {
    const html = preview?.html ?? `<p style="font-family:sans-serif;color:#888">${t('Нет HTML-версии')}</p>`;
    return forceScheme(html, view === 'dark' ? 'dark' : 'light');
  }, [preview, view]);

  return (
    <>
      <Link to="/templates" className="small row" style={{ marginBottom: 12 }}><IconArrowLeft size={14} /> {t('Все шаблоны')}</Link>
      <div className="page-head">
        <div className="grow">
          <h1>Code2Email</h1>
          <p>{t('Опишите письмо словами — получите черновик шаблона: светлая и тёмная тема, текстовая версия, переменные и код отправки. Черновик ничего не отправляет: шаблоном он станет только после публикации.')}</p>
        </div>
        <span className={`pill ${ai?.enabled ? 'ok' : 'suppressed'}`}>{ai?.enabled ? t('AI-доработка текста: {model}', { model: ai.model ?? '' }) : t('Без AI: готовые архетипы')}</span>
      </div>

      <div className="editor-grid">
        <form className="card card-pad stack" onSubmit={generate}>
          <Field label={t('Какое письмо нужно')} hint={t('Например: «код подтверждения входа, живёт 10 минут» или «receipt after payment»')}>
            <textarea className="input" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={2000} disabled={!writable} required minLength={3} />
          </Field>
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
            <Field label={t('Продукт')} hint={t('Имя в шапке и подписи')}>
              <input className="input" value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} maxLength={60} disabled={!writable} />
            </Field>
            <Field label={t('Цвет бренда')} hint={t('Кнопки и ссылки; контраст текста подбирается сам')}>
              <div className="row">
                <input type="color" className="color-swatch" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} disabled={!writable} aria-label={t('Цвет бренда')} />
                <input className="input input-mono" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} maxLength={7} disabled={!writable} />
              </div>
            </Field>
          </div>
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
            <Field label={t('Тип письма')}>
              <select className="select input" value={form.archetype} onChange={(e) => setForm({ ...form, archetype: e.target.value })} disabled={!writable}>
                <option value="">{t('Определить по описанию')}</option>
                {archetypes.data?.map((a) => <option key={a.id} value={a.id}>{t(a.label.ru)}</option>)}
              </select>
            </Field>
            <Field label={t('Язык')}>
              <select className="select input" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} disabled={!writable}>
                <option value="">{t('Как в описании')}</option>
                <option value="ru">{t('Русский')}</option>
                <option value="en">English</option>
              </select>
            </Field>
          </div>
          <label className="row small" style={{ color: ai?.enabled ? 'var(--text-2)' : 'var(--muted)' }}>
            <input
              type="checkbox"
              checked={form.use_ai && Boolean(ai?.enabled)}
              disabled={!ai?.enabled || !writable}
              onChange={(e) => setForm({ ...form, use_ai: e.target.checked })}
              aria-label={ai?.enabled ? t('Доработать текст моделью') : t('AI-доработка текста (выключена)')}
            />
            {ai?.enabled ? t('Доработать текст моделью (вёрстка, ссылки и переменные не меняются)') : t('AI-доработка выключена: не задан ANTHROPIC_API_KEY')}
          </label>
          {writable ? (
            <button className="btn btn-primary" disabled={busy || form.description.trim().length < 3} data-track="template.generate_click"><IconBolt size={16} /> {busy ? t('Собираем черновик…') : t('Сгенерировать черновик')}</button>
          ) : (
            <div className="callout">{t('Роль «наблюдатель» может смотреть черновики, но не создавать их.')}</div>
          )}
        </form>

        <div className="card">
          <div className="card-head"><h3>{t('Черновики')}</h3><span className="small muted">{t('последние 50')}</span></div>
          {drafts.loading ? (
            <div className="card-pad stack"><Skeleton /><Skeleton /></div>
          ) : !drafts.data?.length ? (
            <Empty icon={<IconLayers size={32} />} title={t('Черновиков пока нет')}>{t('Сгенерируйте первый — он появится здесь.')}</Empty>
          ) : (
            <div className="table-wrap">
            <table className="table drafts-table">
              <tbody>
                {drafts.data.map((d) => (
                  <tr key={d.id} className="click" onClick={() => api.get<Draft>(`/code2email/drafts/${d.id}`).then(load)}>
                    <td><strong>{d.name}</strong><div className="small muted"><code>{d.alias}</code> · {d.mode === 'llm' ? t('AI') : t('архетип')}</div></td>
                    <td>{d.published ? <span className="pill ok">{t('опубликован v{v}', { v: d.published.version })}</span> : <span className="pill pending">{t('черновик')}</span>}</td>
                    <td className="small muted hide-sm" style={{ textAlign: 'right' }}>{ago(d.created_at)}</td>
                    <td style={{ width: 40 }}>{writable && <button className="btn btn-ghost icon-btn" aria-label={t('Удалить черновик')} onClick={(e) => { e.stopPropagation(); remove(d.id); }}><IconTrash size={15} /></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {draft && (
        <div className="stack" style={{ marginTop: 16 }}>
          <div className="row wrap">
            <h2 style={{ flex: 1 }}>{t(draft.archetype?.label ?? draft.name)}</h2>
            <span className={`pill ${draft.generator.mode === 'llm' ? 'ok' : 'sent'}`}>{draft.generator.mode === 'llm' ? t('текст: {model}', { model: draft.generator.model ?? '' }) : t('архетип без AI')}</span>
            {draft.alternatives.length > 0 && <span className="small muted">{t('Также подходит: {list}', { list: draft.alternatives.map((a) => t(a.label)).join(', ') })}</span>}
          </div>
          {draft.generator.notice && <div className="callout">{draft.generator.notice}</div>}
          {draft.notes.map((n) => <div key={n} className="callout warn">{n}</div>)}

          <div className="editor-grid">
            <div className="card">
              <div className="card-head">
                <h3>{t('Предпросмотр')}</h3>
                <div className="chips">
                  <button className={`chip${view === 'light' ? ' on' : ''}`} onClick={() => setView('light')}>{t('Светлая')}</button>
                  <button className={`chip${view === 'dark' ? ' on' : ''}`} onClick={() => setView('dark')}>{t('Тёмная')}</button>
                  <button className={`chip${view === 'text' ? ' on' : ''}`} onClick={() => setView('text')}>{t('Текст')}</button>
                </div>
              </div>
              <div className="card-pad stack">
                <div className="small muted">{t('Тема:')} <strong style={{ color: 'var(--text)' }}>{preview?.subject || '—'}</strong></div>
                {view === 'text' ? (
                  <pre className="code" style={{ whiteSpace: 'pre-wrap' }}>{preview?.text ?? t('Нет текстовой версии')}</pre>
                ) : (
                  // sandbox without allow-scripts: generated HTML can never run code in the dashboard
                  <iframe title={t('Предпросмотр письма')} sandbox="" srcDoc={srcDoc} className="mail-frame" style={{ background: view === 'dark' ? '#0b1220' : '#f2f5f9', colorScheme: view === 'dark' ? 'dark' : 'light' }} />
                )}
                {view === 'dark' && <div className="small muted">{t('Эмуляция почтового клиента в тёмной теме (Apple Mail, iOS, Outlook.com). Gmail тёмные правила игнорирует и показывает светлую версию — она тоже читаемая.')}</div>}
                {!!preview?.missing.length && <div className="callout warn">{t('Не хватает переменных: {list}', { list: preview.missing.join(', ') })}</div>}
              </div>
            </div>

            <div className="card card-pad stack">
              <h3>{t('Публикация')}</h3>
              <div className="grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)' }}>
                <Field label={t('Название')} error={errors.name}><input className="input" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} disabled={!writable} /></Field>
                <Field label={t('Алиас')} error={errors.alias}><input className="input input-mono" value={edit.alias} onChange={(e) => setEdit({ ...edit, alias: e.target.value })} disabled={!writable} /></Field>
              </div>
              <Field label={t('Тема')} error={errors.subject}><input className="input" value={edit.subject} onChange={(e) => setEdit({ ...edit, subject: e.target.value })} disabled={!writable} /></Field>
              <details>
                <summary className="small" style={{ cursor: 'pointer', fontWeight: 700 }}>{t('Править HTML и текст')}</summary>
                <div className="stack" style={{ marginTop: 10 }}>
                  <textarea className="input input-mono" rows={10} value={edit.html} onChange={(e) => setEdit({ ...edit, html: e.target.value })} disabled={!writable} spellCheck={false} aria-label="HTML" />
                  <textarea className="input input-mono" rows={5} value={edit.text} onChange={(e) => setEdit({ ...edit, text: e.target.value })} disabled={!writable} aria-label={t('Текст')} />
                </div>
              </details>
              <Field label={t('Куда')} hint={t('Новая версия существующего шаблона сразу начнёт использоваться при отправке по его алиасу')}>
                <select className="select input" value={target} onChange={(e) => setTarget(e.target.value)} disabled={!writable}>
                  <option value="new">{t('Новый шаблон (v1)')}</option>
                  {templates.data?.map((tp) => <option key={tp.id} value={tp.id}>{t('Новая версия: {name} ({alias}, сейчас v{v})', { name: tp.name, alias: tp.alias, v: tp.version })}</option>)}
                </select>
              </Field>
              <div className="small muted">{t('Переменные:')} {draft.variables.map((v) => <code key={v} style={{ marginRight: 6 }}>{v}</code>)}</div>
              {draft.published && <div className="callout ok">{t('Уже опубликован: v{v} ·', { v: draft.published.version })} <Link to={`/templates/${draft.published.template_id}`}>{t('открыть шаблон')}</Link></div>}
              {writable && <button className="btn btn-primary" onClick={publish} data-track="template.publish_click">{t('Опубликовать')}</button>}
            </div>
          </div>

          <div className="card card-pad">
            <h3 style={{ marginBottom: 12 }}>{t('Отправка из кода')}</h3>
            <p className="small muted" style={{ marginBottom: 12 }}>{t('Работает после публикации. Idempotency-Key — по одному на событие (заказ, запрос сброса): повтор запроса не создаст второе письмо.')}</p>
            <CodeTabs snippets={draft.snippets} />
          </div>
        </div>
      )}
    </>
  );
}
